import { logger } from "../utils/logger";
import { useEffect, useState, useMemo, useCallback } from "react";
import type { ReactNode } from "react";
import type { ThemeConfig, ThemeMode, ThemeContextValue } from "../types/theme";
import { defaultTheme, darkTheme, themePresets } from "../config/theme";
import { ThemeContext } from "./theme-context";
import { ThemeService, type ThemeServiceError } from "../services/themeService";
import { validateTheme, sanitizeTheme } from "../utils/themeValidation";

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  onError?: (error: ThemeServiceError) => void;
}

export function ThemeProvider({
  children,
  defaultMode = "light",
  onError,
}: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(defaultMode);
  const [customizations, setCustomizations] = useState<Partial<ThemeConfig>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ThemeServiceError | null>(null);

  // Load initial theme data
  useEffect(() => {
    const loadInitialTheme = async () => {
      try {
        setIsLoading(true);

        // Load theme mode and customizations in parallel
        const [loadedMode, loadedCustomizations] = await Promise.all([
          ThemeService.loadThemeMode(),
          ThemeService.loadCustomizations(),
        ]);

        setMode(loadedMode);
        setCustomizations(loadedCustomizations);
        setError(null);
      } catch (err) {
        const themeError = err as ThemeServiceError;
        setError(themeError);
        onError?.(themeError);
        logger.error("Failed to load theme:", themeError);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialTheme();
  }, [onError]);

  // Determine the effective theme mode
  const effectiveMode =
    mode === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : mode;

  // Get the base theme
  const baseTheme = effectiveMode === "dark" ? darkTheme : defaultTheme;

  // Apply customizations to the base theme
  const theme: ThemeConfig = useMemo(
    () => ({
      ...baseTheme,
      ...customizations,
      colors: {
        ...baseTheme.colors,
        ...customizations.colors,
      },
      typography: {
        ...baseTheme.typography,
        ...customizations.typography,
      },
      spacing: {
        ...baseTheme.spacing,
        ...customizations.spacing,
      },
      borderRadius: {
        ...baseTheme.borderRadius,
        ...customizations.borderRadius,
      },
      shadows: {
        ...baseTheme.shadows,
        ...customizations.shadows,
      },
      transitions: {
        ...baseTheme.transitions,
        ...customizations.transitions,
      },
      zIndex: {
        ...baseTheme.zIndex,
        ...customizations.zIndex,
      },
    }),
    [baseTheme, customizations],
  );

  // Apply CSS custom properties to the document
  useEffect(() => {
    const root = document.documentElement;

    // Set theme mode attribute
    root.setAttribute("data-theme", effectiveMode);

    // Apply color variables
    Object.entries(theme.colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });

    Object.entries(theme.colors.secondary).forEach(([key, value]) => {
      root.style.setProperty(`--color-secondary-${key}`, value);
    });

    Object.entries(theme.colors.accent).forEach(([key, value]) => {
      root.style.setProperty(`--color-accent-${key}`, value);
    });

    Object.entries(theme.colors.gray).forEach(([key, value]) => {
      root.style.setProperty(`--color-gray-${key}`, value);
    });

    // Apply semantic colors
    root.style.setProperty("--color-success", theme.colors.success);
    root.style.setProperty("--color-warning", theme.colors.warning);
    root.style.setProperty("--color-error", theme.colors.error);
    root.style.setProperty("--color-info", theme.colors.info);

    // Apply typography variables
    root.style.setProperty(
      "--font-family-sans",
      theme.typography.fontFamily.sans,
    );
    root.style.setProperty(
      "--font-family-serif",
      theme.typography.fontFamily.serif,
    );
    root.style.setProperty(
      "--font-family-mono",
      theme.typography.fontFamily.mono,
    );

    Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
      root.style.setProperty(`--font-size-${key}`, value);
    });

    // Apply spacing variables
    Object.entries(theme.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-${key}`, value);
    });

    // Apply border radius variables
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--border-radius-${key}`, value);
    });

    // Apply shadow variables
    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Apply transition variables
    Object.entries(theme.transitions).forEach(([key, value]) => {
      root.style.setProperty(`--transition-${key}`, value);
    });

    // Apply z-index variables
    Object.entries(theme.zIndex).forEach(([key, value]) => {
      root.style.setProperty(`--z-${key}`, value.toString());
    });
  }, [theme, effectiveMode]);

  // Save theme mode when it changes
  useEffect(() => {
    if (!isLoading) {
      ThemeService.saveThemeMode(mode).catch((err) => {
        const themeError = err as ThemeServiceError;
        setError(themeError);
        onError?.(themeError);
      });
    }
  }, [mode, isLoading, onError]);

  // Save customizations when they change
  useEffect(() => {
    if (!isLoading && Object.keys(customizations).length > 0) {
      ThemeService.saveCustomizations(customizations).catch((err) => {
        const themeError = err as ThemeServiceError;
        setError(themeError);
        onError?.(themeError);
      });
    }
  }, [customizations, isLoading, onError]);

  // Listen for system theme changes
  useEffect(() => {
    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => {
        // Force re-render by updating a state that affects the theme
        setCustomizations((prev) => ({ ...prev }));
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [mode]);

  const updateTheme = useCallback(
    (updates: Partial<ThemeConfig>) => {
      // Validate updates before applying
      const validation = validateTheme(updates);
      if (!validation.isValid) {
        const validationError: ThemeServiceError = {
          type: "validation",
          message: "Theme validation failed",
          details: validation.errors,
        };
        setError(validationError);
        onError?.(validationError);

        // Apply sanitized version
        const sanitized = sanitizeTheme(updates);
        if (Object.keys(sanitized).length === 0) {
          return; // Nothing valid to apply
        }
        updates = sanitized;
      }

      setCustomizations((prev) => {
        const newCustomizations: Partial<ThemeConfig> = {
          ...prev,
          ...updates,
        };

        if (updates.colors) {
          newCustomizations.colors = {
            ...prev.colors,
            ...updates.colors,
          };
        }

        if (updates.typography) {
          newCustomizations.typography = {
            ...prev.typography,
            ...updates.typography,
          };
        }

        if (updates.spacing) {
          newCustomizations.spacing = {
            ...prev.spacing,
            ...updates.spacing,
          };
        }

        if (updates.borderRadius) {
          newCustomizations.borderRadius = {
            ...prev.borderRadius,
            ...updates.borderRadius,
          };
        }

        if (updates.shadows) {
          newCustomizations.shadows = {
            ...prev.shadows,
            ...updates.shadows,
          };
        }

        if (updates.transitions) {
          newCustomizations.transitions = {
            ...prev.transitions,
            ...updates.transitions,
          };
        }

        if (updates.zIndex) {
          newCustomizations.zIndex = {
            ...prev.zIndex,
            ...updates.zIndex,
          };
        }

        return newCustomizations;
      });

      // Clear any previous errors on successful update
      setError(null);
    },
    [onError],
  );

  const resetTheme = useCallback(async () => {
    try {
      await ThemeService.backupCurrentTheme(customizations);
      setCustomizations({});
      setError(null);
    } catch (err) {
      const themeError = err as ThemeServiceError;
      setError(themeError);
      onError?.(themeError);
    }
  }, [customizations, onError]);

  const applyPreset = useCallback(
    async (presetId: string) => {
      const preset = themePresets[presetId as keyof typeof themePresets];
      if (preset) {
        try {
          // Backup current theme before applying preset
          await ThemeService.backupCurrentTheme(customizations);
          setCustomizations(preset);
          setError(null);
        } catch (err) {
          const themeError = err as ThemeServiceError;
          setError(themeError);
          onError?.(themeError);
        }
      }
    },
    [customizations, onError],
  );

  const contextValue: ThemeContextValue = {
    theme,
    mode,
    setMode,
    updateTheme,
    resetTheme,
    applyPreset,
    isLoading,
    error,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}
